import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import style from "./MagicBgForm.module.scss";
import ClearIcon from "../../../public/editor/close_cross.svg";
import BlueButton from "../UI/BlueButton";
import MagicBgInfoBlock from "./MagicBgInfo";
import { AVAILABLE_LOCALES } from "../../../constants/magic_bg_constants";
import * as ga from "../../../lib/ga";
import { useStore } from "../../../stores/use_global_state";
import { HandySvg } from "handy-svg";
import AspectRatio from "../AspectRatio/AspectRatio";
import ExtendedSettings from "../ExtendedSettings/ExtendedSettings";
import { gaCreditsLimit } from "../../../utils/gaCreditsLimit";
import { NumberOfOutputsCounter, NumberOfOutputsSelector } from "../NumberOfOutputs/NumberOfOutputs";
import StyleSelector from "../StyleSelector/StyleSelector";
import PromptsSelector from "../PromptsSelector/PromptsSelector";
import AIModel from "../AIModel/AIModel";

function MagicBgForm({
	dictionary,
	selectedStyle = {},
	openSelector = () => {},
	isLoading = false,
	submitUserInput = () => {},
	isAdaptive,
	openStyleSelector,
	selectStyle,
	stylesList,
	closeSelector,
	query,
}) {
	const { state, dispatch } = useStore();
	const { locale } = useRouter();

	const [userLang, setUserLang] = useState(() => {
		return AVAILABLE_LOCALES.includes(locale) ? locale : "en";
	});

	const pattern = /_/gi;
	const [userInput, setUserInput] = useState(state.textToImgTask?.prompt || query?.promt?.replace(pattern, " ") || "");
	const [inputError, setInputError] = useState(false);
	const [openInfoWindow, setOpenInfoWindow] = useState(false);
	const [extendedSettingsIsOpen, setExtendedSettingsIsOpen] = useState(false);
	const [showAdaptiveSettings, setShowAdaptiveSettings] = useState(false);
	const [settings, setSettings] = useState({
		negative_prompt: state.textToImgTask?.negative_prompt || null,
		num_results: { name: state.accountData?.total_credits < 2 ? "1" : "2", value: state.accountData?.total_credits < 2 ? 1 : 2 },
		out_ar: { name: "(1:1)", value: "1:1", dictionaryKey: "square" },
		seed: null,
		sampler: state.textToImgTask?.sampler || null,
		model: state.textToImgTask?.model || null,
		prompt_strength: 7.5,
		steps: 20,
	});

	const openSettingsForGa = useRef(false);
	const formItem = useRef(null);
	const textAreaRef = useRef(null);

	const styleBtnTitle = selectedStyle?.isNoStyle || selectedStyle?.name === undefined ? `${dictionary["Select style"] || "Select style"}` : `${dictionary[selectedStyle.name] || selectedStyle.name}`;

	const submitHandler = (e) => {
		e.preventDefault();

		if (state.isAuth) {
			if (state?.accountData?.plan === "free" && state?.accountData?.total_credits < settings.num_results.value) {
				gaCreditsLimit("IMAGEGEN");
				dispatch({ type: "setVideoError", error_msg: "free_limit_img", error_code: "free_limit_img" });
				return false;
			}

			if ((state?.accountData?.plan === "pro" || state?.accountData?.plan === "promo") && state?.accountData?.total_credits < settings.num_results.value) {
				gaCreditsLimit("IMAGEGEN");
				dispatch({ type: "setVideoError", error_msg: "pro_limit", error_code: "pro_limit" });
				return false;
			}
		}

		if (userInput.trim().length !== 0) {
			ga.event({ action: `TextToImg page: click 'Create'`, params: { event_category: "TextToImg page" } });

			setExtendedSettingsIsOpen(false);

			submitUserInput(userInput?.trim(), userLang, settings, null);
		} else {
			setInputError(true);
		}
	};

	const gaOpenAdvancedSettings = () => {
		ga.event({ action: `TextToImg page: click 'Advanced settings'`, params: { event_category: "TextToImg page" } });
	};

	const specificStyleHandler = () => {
		openSelector();
	};

	useEffect(() => {
		setUserLang(() => {
			return AVAILABLE_LOCALES.includes(locale) ? locale : "en";
		});
	}, [locale]);

	useEffect(() => {
		if (localStorage.nonAuthAi && state.isAuth) {
			try {
				JSON.parse(localStorage.nonAuthAi);
			} catch (e) {
				return;
			}
			const preSavedData = JSON?.parse(localStorage.nonAuthAi);
			setUserInput(preSavedData.userInput);
			setUserLang(preSavedData.userLang);
			setSettings(preSavedData.settings);
		}
	}, [state.isAuth]);

	const autoFocusFn = useCallback((element) => (element ? element.focus() : null), []);

	const ExtendedSettingsToggle = () => {
		if (openSettingsForGa.current === null) {
			openSettingsForGa.current = true;
			gaOpenAdvancedSettings();
		}
		setExtendedSettingsIsOpen(!extendedSettingsIsOpen);
	};

	const changeSettings = (item, type) => {
		switch (type) {
			case "aspect_ratio":
				setSettings({ ...settings, out_ar: item });
				break;
			case "ai_model":
				setSettings({ ...settings, model: item });
				break;
			case "negative_prompt":
				setSettings({ ...settings, negative_prompt: item });
				break;
			case "num_results":
				setSettings({ ...settings, num_results: item });
				break;
			case "seed":
				setSettings({ ...settings, seed: item });
				break;
			case "sampler":
				setSettings({ ...settings, sampler: item });
				break;
			case "prompt_strength":
				setSettings({ ...settings, prompt_strength: item });
				break;
			case "steps":
				setSettings({ ...settings, steps: item });
				break;
			default:
				break;
		}
	};

	useEffect(
		() => {
			if (state.textToImgTask) changeSettings(state.textToImgTask?.negative_prompt, "negative_prompt");
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[state.textToImgTask]
	);

	useEffect(
		() => {
			if (userInput?.trim() === "") {
				changeSettings("", "negative_prompt");
			} else {
				setShowAdaptiveSettings(true);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[userInput]
	);

	const changePromptValue = ({ target }) => {
		setUserInput(target.value);
		setInputError(false);
	};

	const promptProps = { className: style.input, id: "prompt-input", placeholder: dictionary["eg bright universe"] };

	return (
		<div className={`${style["form-wrapper"]} ${showAdaptiveSettings ? style.adaptiveSettings : ""}`}>
			<form
				onSubmit={submitHandler}
				id={`${inputError ? "form-error" : "form"}`}
				className={`${style.form} ${inputError ? style.error : ""}`}
				onClick={({ currentTarget }) => currentTarget.scrollIntoView({ behavior: "smooth" })}
				ref={formItem}
			>
				<input {...promptProps} type="text" disabled={isLoading} value={userInput} onChange={changePromptValue} ref={autoFocusFn} />
				<textarea {...promptProps} disabled={isLoading} value={userInput} onFocus={() => setShowAdaptiveSettings(true)} onChange={changePromptValue} ref={textAreaRef} />
				{!!userInput?.trim() ? <ClearIcon className={`${style.clearIcon} ${isLoading ? style.disabled : ""}`} onClick={() => setUserInput("")} /> : null}

				<div className={style.form_ui}>
					<NumberOfOutputsSelector style={style} activeItem={settings.num_results} dictionary={dictionary} isLoading={isLoading} changeSettings={changeSettings} />
					<BlueButton id="submit-btn" data-ga-value="Create" disabled={isLoading} isGradient={true} type={"submit"}>
						<span className={style.desktopBtn}>{(dictionary["create_for_1_credit"] || "Create for 1 credit").replace("1", settings.num_results.value)}</span>
					</BlueButton>
				</div>
			</form>
			<PromptsSelector changeSettings={changeSettings} changePrompt={setUserInput} />
			<div className={style["settings-default-wrapper"]}>
				<div className={style.adaptiveModelWrapper}>
					<AIModel style={style} activeItem={settings.model} dictionary={dictionary} isLoading={isLoading} changeSettings={changeSettings} />
				</div>
				<div className={style[`settings-default-item`]}>
					<p className={style[`title`]}>{dictionary["style"] || "Style"}</p>
					<StyleSelector isShown={openStyleSelector} dictionary={dictionary} selectedStyle={selectedStyle} closeSelector={(e) => closeSelector(e)} selectStyle={selectStyle} stylesList={stylesList} />
					<button id="style-selector-btn" className={`${style[`default-btn`]} ${style.openStylesBtn}`} disabled={isLoading} onClick={specificStyleHandler}>
						{styleBtnTitle}
						<HandySvg src={"/workspace/profile/profile_arrow.svg"} width={13} height={8} className={style.icon} />
					</button>
				</div>
				<AspectRatio style={style} dictionary={dictionary} activeItem={settings.out_ar} isLoading={isLoading} changeSettings={changeSettings} />
			</div>
			<ExtendedSettings
				isOpen={extendedSettingsIsOpen}
				toggleOpen={() => setExtendedSettingsIsOpen(!extendedSettingsIsOpen)}
				style={style}
				dictionary={dictionary}
				settings={settings}
				isLoading={isLoading}
				changeSettings={changeSettings}
				isAdaptive={isAdaptive}
			/>
			{openInfoWindow ? <MagicBgInfoBlock dictionary={dictionary} style={style} /> : null}
			<div className={style.form_ui_adaptive}>
				{showAdaptiveSettings ? <NumberOfOutputsCounter style={style} activeItem={settings.num_results} dictionary={dictionary} isLoading={isLoading} changeSettings={changeSettings} /> : null}

				<BlueButton id="submit-btn" data-ga-value="Create" disabled={isLoading} isGradient={true} btnClickHandler={submitHandler}>
					<span className={style.adaptiveBtn}>{(dictionary["create_for_1_credit"] || "Create for 1 credit").replace("1", settings.num_results.value)}</span>
				</BlueButton>
			</div>
		</div>
	);
}

export default MagicBgForm;
